import { useEffect, useState } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, btn, btnGhost, label, inputStyle } from "../roads/_roadsCommon";

/** Ch 23.2 — Earthwork visualizer (cut/fill between original surface + design pad). */
export default function EarthworkVizPanel({ projectId }) {
  const [terrains, setTerrains] = useState([]);
  const [terrainId, setTerrainId] = useState("");
  const [bbox, setBbox] = useState({ x0: 0, y0: 0, x1: 100, y1: 100 });
  const [elevation, setElevation] = useState(98);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => { (async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/terrain`);
      setTerrains(data.terrains || []);
      if (data.terrains?.[0]) setTerrainId(data.terrains[0].id);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  })(); /* eslint-disable-next-line */ }, []);

  const compute = async () => {
    if (!terrainId) return alert("Select a terrain surface.");
    try {
      const { data } = await api.post(
        `/projects/${projectId}/terrain/${terrainId}/earthwork`,
        { bbox: [bbox.x0, bbox.y0, bbox.x1, bbox.y1],
          design_elevation_m: elevation });
      setResult(data);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  // ── File 53: Mass-haul curve ──
  const [massHaul, setMassHaul] = useState(null);
  const computeMassHaul = async () => {
    setErr("");
    try {
      const { data } = await api.post(
        `/projects/${projectId}/earthwork/mass-haul`, {});
      setMassHaul(data);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  return (
    <div data-testid="earthwork-viz" style={{ display: "grid",
            gridTemplateColumns: "320px 1fr 280px", gap: 8, height: "calc(100vh - 280px)", minHeight: 700 }}>
      <div style={{ ...card, padding: 8 }}>
        <div style={label}>Earthwork between surfaces</div>
        <div style={{ marginTop: 8, fontSize: 10 }}>Terrain</div>
        <select data-testid="ew-terrain" style={inputStyle}
          value={terrainId} onChange={(e) => setTerrainId(e.target.value)}>
          <option value="">— pick —</option>
          {terrains.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <div style={{ marginTop: 4, fontSize: 10 }}>BBox (x₀, y₀, x₁, y₁)</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
          <input data-testid="ew-x0" style={inputStyle} type="number" value={bbox.x0}
            onChange={(e) => setBbox({ ...bbox, x0: parseFloat(e.target.value) || 0 })} />
          <input data-testid="ew-y0" style={inputStyle} type="number" value={bbox.y0}
            onChange={(e) => setBbox({ ...bbox, y0: parseFloat(e.target.value) || 0 })} />
          <input data-testid="ew-x1" style={inputStyle} type="number" value={bbox.x1}
            onChange={(e) => setBbox({ ...bbox, x1: parseFloat(e.target.value) || 0 })} />
          <input data-testid="ew-y1" style={inputStyle} type="number" value={bbox.y1}
            onChange={(e) => setBbox({ ...bbox, y1: parseFloat(e.target.value) || 0 })} />
        </div>
        <div style={{ marginTop: 4, fontSize: 10 }}>Design elevation (m)</div>
        <input data-testid="ew-elev" style={inputStyle} type="number" step="0.1"
          value={elevation} onChange={(e) =>
            setElevation(parseFloat(e.target.value) || 0)} />
        <button data-testid="ew-compute"
          style={{ ...btn, width: "100%", marginTop: 12 }}
          onClick={compute}>Compute earthwork</button>
        <button data-testid="ew-masshaul-btn"
          style={{ ...btn, width: "100%", marginTop: 6, background: "#16a34a" }}
          onClick={computeMassHaul}>Compute Mass-Haul</button>
        {massHaul && massHaul.summary && (
          <div data-testid="ew-masshaul-result" style={{ marginTop: 12, fontSize: 11 }}>
            <div style={{ ...label, marginTop: 6 }}>Mass-Haul Summary</div>
            <div style={{ display: "grid", gap: 4, marginTop: 4 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "#aaa" }}>Cut</span>
                <span style={{ color: "#ef4444", fontFamily: "monospace" }}>
                  {(massHaul.summary.total_cut_m3 / 1000).toFixed(1)} k m³
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "#aaa" }}>Fill (adj)</span>
                <span style={{ color: "#3aa0ff", fontFamily: "monospace" }}>
                  {(massHaul.summary.total_fill_adjusted_m3 / 1000).toFixed(1)} k m³
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "#aaa" }}>Balance pts</span>
                <span style={{ fontFamily: "monospace" }}>{massHaul.summary.balance_point_count}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "#aaa" }}>Borrow</span>
                <span style={{ color: "#FFB000", fontFamily: "monospace" }}>
                  {(massHaul.summary.borrow_required_m3 / 1000).toFixed(1)} k m³
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "#aaa" }}>Disposal</span>
                <span style={{ color: "#FFB000", fontFamily: "monospace" }}>
                  {(massHaul.summary.disposal_required_m3 / 1000).toFixed(1)} k m³
                </span>
              </div>
              <div style={{ marginTop: 6, padding: 6, background: "rgba(255,176,0,0.1)", borderRadius: 4, fontSize: 10 }}>
                {massHaul.summary.balance}
              </div>
            </div>
            {/* Mini mass-haul curve */}
            {massHaul.mass_haul_ordinates?.length > 0 && (
              <svg viewBox="0 -120 600 240" style={{ width: "100%", marginTop: 8, background: "#0a1220" }}>
                <line x1="0" y1="0" x2="600" y2="0" stroke="#444" strokeWidth="0.5" />
                {(() => {
                  const pts = massHaul.mass_haul_ordinates;
                  const max = Math.max(...pts.map(p => Math.abs(p.mass_ordinate_m3)), 1);
                  const xs = pts.length;
                  const path = pts.map((p, i) => {
                    const x = (i / (xs - 1)) * 600;
                    const y = -(p.mass_ordinate_m3 / max) * 100;
                    return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
                  }).join(" ");
                  return <path d={path} stroke="#16a34a" strokeWidth="2" fill="none" />;
                })()}
              </svg>
            )}
          </div>
        )}
      </div>

      <div style={{ ...card, padding: 16, overflowY: "auto" }}>
        {!result ? (
          <div style={{ color: "var(--pf-muted)", fontSize: 12 }}>
            Pick a terrain, set the design pad bbox + target elevation, and compute.
          </div>
        ) : (
          <div data-testid="ew-results">
            <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
              <Stat label="Cut" value={`${(result.cut_m3 / 1000).toFixed(2)} k m³`} colour="#ef4444" />
              <Stat label="Fill" value={`${(result.fill_m3 / 1000).toFixed(2)} k m³`} colour="#3aa0ff" />
              <Stat label="Net (fill − cut)"
                value={`${(result.balance_m3 / 1000).toFixed(2)} k m³`}
                colour={result.balance_m3 >= 0 ? "#3aa0ff" : "#ef4444"} />
              <Stat label="Cells" value={result.cells} colour="#888" />
            </div>
            <div style={{ ...label }}>Bbox sketch (cut = red, fill = blue, balance = grey)</div>
            <svg viewBox={`-20 -${(bbox.y1 - bbox.y0) + 20} ${(bbox.x1 - bbox.x0) + 40} ${(bbox.y1 - bbox.y0) + 40}`}
                  style={{ width: "100%", maxHeight: 360, background: "#0a1220", marginTop: 8 }}>
              <rect x={0} y={-(bbox.y1 - bbox.y0)} width={bbox.x1 - bbox.x0} height={bbox.y1 - bbox.y0}
                      fill={result.balance_m3 > 0 ? "rgba(58,160,255,0.25)"
                              : result.balance_m3 < 0 ? "rgba(239,68,68,0.25)" : "#444"}
                      stroke="#F26B1F" strokeWidth={1} />
              <text x={(bbox.x1 - bbox.x0) / 2} y={-(bbox.y1 - bbox.y0) / 2}
                     textAnchor="middle" fill="#fff" fontSize={Math.max(2, (bbox.x1 - bbox.x0) / 30)}
                     fontFamily="monospace">
                target {elevation} m
              </text>
            </svg>
            <p style={{ fontSize: 11, color: "#aaa", marginTop: 16, lineHeight: 1.6 }}>
              Volumes are computed as Σ(cell area × Δz_avg) over a {result.cell_m}m grid sampled
              from the original terrain (IDW interpolation). For a sloped design surface, replace
              the constant elevation with a tilted plane fn (future enhancement).
            </p>
          </div>
        )}
        {err && <div style={{ marginTop: 8, color: "#FF8888", fontSize: 11 }}>{err}</div>}
      </div>

      <div style={{ ...card, padding: 8 }}>
        <div style={label}>Method</div>
        <p style={{ fontSize: 10, color: "#aaa", lineHeight: 1.6 }}>
          Cell-by-cell average-end-area: V<sub>cell</sub> = Δz<sub>avg</sub> × A<sub>cell</sub>.
          When design surface is below original → Cut, above → Fill. The cumulative
          volumes are reported in m³ + k m³ for human readability.
        </p>
      </div>
    </div>
  );
}

function Stat({ label, value, colour }) {
  return (
    <div>
      <div style={{ fontSize: 9, color: "#888", textTransform: "uppercase",
                     letterSpacing: 0.6, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 22, color: colour, fontFamily: "monospace" }}>{value}</div>
    </div>
  );
}
